import { lazy, Suspense, type ComponentType } from "react";
import { createBrowserRouter, type RouteObject } from "react-router";
import { AppShell } from "@/components/shell/Page";
import { PlatformShell } from "@/features/platform/PlatformShell";
import { AuthCallback } from "./AuthCallback";
import { HelpPage, NotFoundPage } from "./StaticPages";
import { RouteError } from "./RouteError";
import { ScreenLoading } from "./ScreenLoading";

function screen(load: () => Promise<{ default: ComponentType }>) {
  const Screen = lazy(load);
  return (
    <Suspense fallback={<ScreenLoading />}>
      <Screen />
    </Suspense>
  );
}

export const routes: RouteObject[] = [
  { path: "/auth/callback", element: <AuthCallback /> },
  // Before anyone is in: sign in, accept an invite, choose a password, or learn why not.
  {
    path: "/sign-in",
    errorElement: <RouteError />,
    element: screen(() => import("@/features/access/SignInPage")),
  },
  {
    path: "/forgot-password",
    errorElement: <RouteError />,
    element: screen(() =>
      import("@/features/access/PasswordPages").then((m) => ({ default: m.ForgotPasswordPage })),
    ),
  },
  {
    path: "/reset-password/:token",
    errorElement: <RouteError />,
    element: screen(() =>
      import("@/features/access/PasswordPages").then((m) => ({ default: m.ResetPasswordPage })),
    ),
  },
  {
    path: "/invite/:token",
    errorElement: <RouteError />,
    element: screen(() => import("@/features/access/InvitePage")),
  },
  {
    path: "/no-access",
    errorElement: <RouteError />,
    element: screen(() => import("@/features/access/NoAccessPage")),
  },
  // The platform console, for superadmins.
  {
    path: "/platform",
    element: <PlatformShell />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: screen(() => import("@/features/platform/TenantsPage")) },
      { path: "tenants/:slug", element: screen(() => import("@/features/platform/TenantPage")) },
      { path: "team", element: screen(() => import("@/features/platform/TeamPage")) },
      { path: "audit", element: screen(() => import("@/features/platform/AuditPage")) },
    ],
  },
  {
    path: "/m/:approvalId?",
    errorElement: <RouteError />,
    element: screen(() => import("@/features/phone/PhonePage")),
  },
  {
    element: <AppShell />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: screen(() => import("@/features/home/HomePage")) },
      {
        path: "needs-you/:approvalId?",
        element: screen(() => import("@/features/needs-you/NeedsYouPage")),
      },
      { path: "cases", element: screen(() => import("@/features/cases/CasesPage")) },
      { path: "cases/:number", element: screen(() => import("@/features/case/CasePage")) },
      {
        path: "war-rooms/:roomId",
        element: screen(() => import("@/features/war-room/WarRoomPage")),
      },
      { path: "flows", element: screen(() => import("@/features/flows/FlowsPage")) },
      { path: "templates", element: screen(() => import("@/features/templates/TemplatesPage")) },
      {
        path: "templates/:templateKey",
        element: screen(() => import("@/features/templates/TemplatePreviewPage")),
      },
      { path: "flows/*", element: screen(() => import("@/features/flow-studio/FlowStudioPage")) },
      { path: "autonomy", element: screen(() => import("@/features/autonomy/AutonomyPage")) },
      { path: "crew", element: screen(() => import("@/features/crew/CrewPage")) },
      { path: "agents/:agentId", element: screen(() => import("@/features/agent/AgentPage")) },
      { path: "guardian", element: screen(() => import("@/features/guardian/GuardianPage")) },
      { path: "outcomes", element: screen(() => import("@/features/outcomes/OutcomesPage")) },
      { path: "ask/:conversationId?", element: screen(() => import("@/features/ask/AskPage")) },
      {
        path: "connections",
        element: screen(() => import("@/features/connections/ConnectionsPage")),
      },
      { path: "settings", element: screen(() => import("@/features/settings/SettingsPage")) },
      { path: "knowledge", element: screen(() => import("@/features/knowledge/KnowledgePage")) },
      { path: "estate", element: screen(() => import("@/features/estate/EstatePage")) },
      { path: "help", element: <HelpPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

export const createRouter = () => createBrowserRouter(routes);
